import React, { use, useCallback, useEffect, useMemo, useState } from "react";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useMutation } from "@apollo/client/react";
import { GET_MISSION_WITH_TERMINATION } from "../../graphql/core";
import ScreenCard, { ButtonType, ScreenCardOptionalAction } from "../ScreenCard";
import { useI18n } from "../../hooks/i18n-hooks";
import { ContractSignatureStatus, ProfileType } from "../../__generated__/graphql";
import Loading from "../../components/Loading";
import PdfAssetViewer from "../../components/PdfAssetViewer";
import { useNavigationHeader, useShowHideHeader } from "../../hooks/use-navigation-header";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { Platform, View } from "react-native";
import MarkdownView from "../../components/MarkdownView";
import { isScrollEnabledForPdfComponent } from "../../utils/pdf-scroll";
import { useCurrentProfile } from "../../hooks/use-current-profile";
import { useSetAlert } from "../../hooks/use-set-alert";
import { DELETE_AMENDMENT, GET_MISSION_AMENDMENTS } from "../../graphql/amendment";
import { useApolloErrorSnackbar, useSnackbar } from "../../hooks/snackbar";
import { useIntercom } from "../../hooks/intercom";
import { useContract } from "../../hooks/use-contract";
import { getContractLabelKind, sleep } from "../../utils";
import { SIGN_PENDING_CONTRACTS } from "../../graphql/signature";
import { FIND_MY_MISSIONS } from "../../graphql/home";
import client from "../../configs/graphql.config";

type ContractPreviewScreenProps = StackScreenProps<RootStackParamList, "ContractPreviewScreen">;

type ButtonsConfig = {
    canSign?: boolean;
    canSignOther?: boolean; // this is used when there is multiple employees in the client
    canSignLater?: boolean;
    canClose?: boolean;
};

/**
 * This screen is used to check if the contract is ready to be signed
 * [ButtonsConfig, labelText, infoText]
 */
const ERRONEOUS_VALUE: [ButtonsConfig, string?] = [{ canClose: true }, undefined];

const STATUS_BUTTON_MAP: Record<ContractSignatureStatus | "none", [ButtonsConfig?, string?]> = {
    none: [],
    canceled_by_lse: ERRONEOUS_VALUE,
    canceled_by_other: ERRONEOUS_VALUE,
    canceled_by_self: ERRONEOUS_VALUE,
    failed: ERRONEOUS_VALUE,
    need_seal: ERRONEOUS_VALUE,
    success: ERRONEOUS_VALUE,
    need_other_sign: [{ canClose: true }, undefined],
    handwritten_in_process: [{ canClose: true }, undefined],
    need_self_sign: [{ canSign: true, canSignLater: true, canSignOther: true }, undefined],
    waiting_self_sign: [{ canSign: true, canSignLater: true, canSignOther: true }, undefined],
    waiting_contract: [undefined, "screens.contract_preview.valid_info"],
};

const ContractPreviewScreen: React.FC<ContractPreviewScreenProps> = ({ route, navigation }) => {
    const { profileType } = use(ProfileSwitcherManagerContext);
    const currentProfile = useCurrentProfile();
    const i18n = useI18n();
    const { contractId, contractType = "contract" } = route.params;
    const [clientAssetId, setClientAssetId] = useState<string>();
    const {
        contract,
        asset,
        error: getContractError,
        loading: getContractLoading,
    } = useContract(contractId, contractType);

    const [deleteAmendment, { loading: deleteAmendmentLoading, error: deleteAmendmentError }] =
        useMutation(DELETE_AMENDMENT);

    // The signature error is not aggregated here: `signNow` already reports it with its
    // own snackbar, and this `error` also drives a headband about fetching the contract.
    const [signPendingContracts] = useMutation(SIGN_PENDING_CONTRACTS);

    // Covers the whole `signNow` flow (mutation, settle delay, refetch, navigation), not
    // just the mutation, so the button stays validating until the screen is popped.
    const [isSigning, setIsSigning] = useState(false);

    const error = getContractError || deleteAmendmentError;
    const loading = getContractLoading || deleteAmendmentLoading || isSigning;

    useApolloErrorSnackbar(error);

    // Capture the first asset id reported by the contract query and keep it
    // even if `asset` is refetched later. Kept as an effect rather than an
    // "adjust state on prop change" render-phase block because the guard there
    // is false on the first render (prev seeded equal to current), which would
    // skip the initial set on Apollo cache-and-network hits.
    useEffect(() => {
        const clientAssetIdFromContract = asset?.id;
        if (
            !clientAssetId &&
            clientAssetIdFromContract &&
            clientAssetIdFromContract !== clientAssetId
        ) {
            setClientAssetId(clientAssetIdFromContract);
        }
    }, [clientAssetId, asset?.id]);

    const signatureStatus = contract?.signatureStatus;

    const [buttonsConfig, infoText] = STATUS_BUTTON_MAP[signatureStatus || "none"];

    const missionId = contract?.missionInfo?.id || "";

    const { showSnackbar } = useSnackbar();

    const signNow = useCallback(async () => {
        setIsSigning(true);
        try {
            const { data } = await signPendingContracts({
                variables: { contractIds: [contractId] },
                refetchQueries: [GET_MISSION_WITH_TERMINATION],
                // Keep the button in its loading state until the refetched mission is in
                // the cache, so the screen we go back to never renders a stale status.
                awaitRefetchQueries: true,
            });
            // A refused document is a per-item outcome, not a GraphQL error: without
            // this the screen reported a success for a signature that never happened
            // and sent the user back to a document still waiting for them.
            if (!data?.signPendingContracts.scheduledCount) {
                throw new Error("Signature was not scheduled");
            }
            // The mutation only schedules the signature server-side: leave it a beat
            // to be applied before re-reading the missions list.
            await sleep(1000);
            void client.refetchQueries({ include: [FIND_MY_MISSIONS] });
            showSnackbar({
                mode: "success",
                text: i18n.t("screens.contract_preview.sign_success"),
            });
            if (contractType === "client_framework") {
                navigation.popTo("Home", {
                    screen: "ProfileStack",
                    params: {
                        screen: "Profile",
                    },
                });
            } else {
                navigation.popTo("Home", {
                    screen: "MissionsScreen",
                    params: {
                        screen: "MissionList",
                    },
                });
            }
        } catch (_error) {
            // Only reset on failure: on success the screen is popped right after.
            setIsSigning(false);
            showSnackbar({
                mode: "error",
                text: i18n.t("screens.contract_preview.sign_error"),
            });
        }
    }, [signPendingContracts, contractId, navigation, contractType, showSnackbar, i18n]);

    const signLater = useCallback(() => {
        if (["contract", "amendment"].includes(contractType)) {
            navigation.popTo("Home", {
                screen: "MissionsScreen",
                params: {
                    screen: "MissionDetails",
                    params: {
                        missionId,
                    },
                },
            });
        } else if (contractType === "client_framework") {
            navigation.popTo("Home", {
                screen: "ProfileStack",
                params: {
                    screen: "Profile",
                },
            });
        }
    }, [contractType, missionId, navigation]);

    const close = useCallback(() => {
        navigation.reset({
            index: 0,
            routes: [{ name: "Home" }],
        });
    }, [navigation]);

    const signOther = useCallback(() => {
        navigation.navigate("MissionSelectSignerScreen", {
            missionId,
            ...(contractType === "amendment" ? { amendmentId: contractId } : {}),
        });
    }, [contractId, contractType, missionId, navigation]);

    const hasSignButton =
        buttonsConfig?.canSign &&
        (profileType === ProfileType.Worker || currentProfile?.canSignContracts);

    const { setAlert } = useSetAlert();

    const _deleteAmendment = useCallback(() => {
        setAlert({
            title: i18n.t("screens.contract_preview.cancel_amendment"),
            message: i18n.t("screens.contract_preview.cancel_amendment_description"),
            buttons: [
                {
                    isPreferred: true,
                    style: "destructive",
                    text: i18n.t("confirm"),
                    onPress: () => {
                        void (async () => {
                            await deleteAmendment({
                                variables: { amendmentId: contractId },
                                refetchQueries: [
                                    GET_MISSION_WITH_TERMINATION,
                                    GET_MISSION_AMENDMENTS,
                                ],
                            });
                            showSnackbar({
                                mode: "success",
                                text: i18n.t("screens.contract_preview.cancel_amendment_success"),
                            });
                            navigation.navigate("MissionDetails", { missionId });
                        })();
                    },
                },
                {
                    style: "cancel",
                    text: i18n.t("back"),
                },
            ],
        });
    }, [setAlert, i18n, deleteAmendment, contractId, showSnackbar, navigation, missionId]);

    const goToCancelAmendment = useCallback(() => {
        navigation.navigate("AmendmentCancellation", {
            amendmentId: contractId,
            mode: profileType === ProfileType.Client ? "cancel" : "reject",
        });
    }, [contractId, navigation, profileType]);

    const { presentMessageComposer } = useIntercom();

    const [buttons, optionalActions] = useMemo<[ButtonType[], ScreenCardOptionalAction[]]>(() => {
        const canDeleteAmendment = profileType === ProfileType.Client && buttonsConfig?.canSign;
        const canCancelAmendment =
            profileType === ProfileType.Client &&
            contract?.signatureStatus === ContractSignatureStatus.NeedOtherSign;
        const canRejectAmendment = profileType === ProfileType.Worker && buttonsConfig?.canSign;
        const canContactAssistance =
            !canDeleteAmendment && !canCancelAmendment && !canRejectAmendment;

        const _all = [
            ...(hasSignButton
                ? [
                      {
                          title: i18n.t("screens.contract_preview.sign_contract"),
                          mode: "secondary" as const,
                          validating: loading,
                          onPress: signNow,
                      },
                  ]
                : []),
            ...(buttonsConfig?.canSignOther &&
            profileType === ProfileType.Client &&
            !contract?.missionInfo?.clientInfo?.isPrivateClient &&
            contractType !== "client_framework"
                ? [
                      {
                          title: i18n.t("screens.contract_preview.other_sign_contract"),
                          mode: hasSignButton ? ("outlined" as const) : ("secondary" as const),
                          onPress: signOther,
                      },
                  ]
                : []),
            ...(buttonsConfig?.canSignLater
                ? [
                      {
                          title: i18n.t("screens.contract_preview.postpone_signing"),
                          mode: "outlined" as const,
                          onPress: signLater,
                      },
                  ]
                : []),
            ...(buttonsConfig?.canClose
                ? [
                      {
                          title: i18n.t("close"),
                          mode: "outlined" as const,
                          onPress: close,
                      },
                  ]
                : []),
            ...(contractType === "amendment"
                ? canDeleteAmendment
                    ? [
                          {
                              title: i18n.t("screens.contract_preview.cancel_amendment"),
                              mode: "danger" as const,
                              onPress: _deleteAmendment,
                          },
                      ]
                    : canCancelAmendment
                      ? [
                            {
                                title: i18n.t("screens.contract_preview.cancel_amendment"),
                                mode: "danger" as const,
                                onPress: goToCancelAmendment,
                            },
                        ]
                      : canRejectAmendment
                        ? [
                              {
                                  title: i18n.t("screens.contract_preview.refuse_amendment"),
                                  mode: "danger" as const,
                                  onPress: goToCancelAmendment,
                              },
                          ]
                        : canContactAssistance
                          ? [
                                {
                                    title: i18n.t("assistance"),
                                    mode: "secondary" as const,
                                    onPress: presentMessageComposer,
                                },
                            ]
                          : []
                : []),
        ];

        if (_all.length > 2) {
            const other = _all.splice(1).map((b) => ({
                label: b.title,
                onPress: b.onPress,
            }));
            const first = _all[0];
            return [[first], other];
        }
        return [_all, []];
    }, [
        profileType,
        buttonsConfig,
        contract?.signatureStatus,
        contract?.missionInfo?.clientInfo?.isPrivateClient,
        hasSignButton,
        i18n,
        loading,
        signNow,
        signOther,
        signLater,
        close,
        contractType,
        _deleteAmendment,
        goToCancelAmendment,
        presentMessageComposer,
    ]);

    const contractLabelKind = getContractLabelKind(
        profileType,
        contract?.missionInfo?.clientInfo?.useLse,
    );

    useNavigationHeader(
        contractType === "contract"
            ? i18n.t(`contract.pdf_prefix.${contractLabelKind}`)
            : contractType === "amendment"
              ? i18n.t("mission.amendment.title")
              : i18n.t("profile.framework_contract"),
    );

    // Hide the header on web to avoid conflicts with the PDF viewer
    useShowHideHeader(Platform.OS === "web" ? "hide" : undefined);

    return (
        <ScreenCard
            optionalActions={optionalActions}
            buttons={buttons}
            relativeActions={Platform.OS === "web"}
            headband={
                error
                    ? {
                          text: i18n.t("screens.contract_preview.error"),
                          mode: "error",
                      }
                    : undefined
            }
            scrollEnabled={isScrollEnabledForPdfComponent("ScreenCard")}
            isContentContainerFlex
        >
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            {(paddingBottom: number) => {
                return clientAssetId ? (
                    <PdfAssetViewer
                        assetId={clientAssetId}
                        waitingDescription={i18n.t("screens.contract_preview.loading_document")}
                        // iOS: the bottom action bar is a translucent blur and the PDF owns its
                        // scroll, so it must fill the whole view and stay visible behind the bar.
                        // The bar height is applied as a native scroll inset (contentInsetBottom)
                        // rather than padding, so the end of the last page can still be scrolled
                        // clear of the bar. Android/web: the bar is opaque, so keep the PDF above
                        // it via padding.
                        {...(Platform.OS === "ios"
                            ? { contentInsetBottom: paddingBottom }
                            : { style: { paddingBottom }, contentInsetBottom: 0 })}
                        scrollEnabled={isScrollEnabledForPdfComponent("PdfViewer")}
                    />
                ) : infoText ? (
                    <View style={{ flex: 1, justifyContent: "space-evenly" }}>
                        {infoText && (
                            <MarkdownView
                                markdownStyle={{
                                    textgroup: { textAlign: "center" },
                                }}
                                markdown={i18n.t(infoText)}
                            />
                        )}
                    </View>
                ) : (
                    <Loading />
                );
            }}
        </ScreenCard>
    );
};

export default ContractPreviewScreen;
